import React, { useState } from 'react';
import {
  Modal,
  Form,
  InputNumber,
  Select,
  Button,
  Space,
  Typography,
  Divider,
  Table,
  Tag,
  Progress,
  Input,
  Tooltip,
  Popconfirm,
  message,
  Row,
  Col,
  Alert,
  Card,
} from 'antd';
import {
  DollarOutlined,
  DeleteOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import API from '../config/api';
import { Billing, PaymentTransaction } from './type';

const { Text, Title } = Typography;
const { Option } = Select;

interface PaymentPlanModalProps {
  visible: boolean;
  onCancel: () => void;
  billing: Billing;
  onUpdated?: (updatedBilling: Billing) => void;
}

const STATUS_COLORS: Record<string, string> = {
  paid: 'success',
  partial: 'warning',
  pending: 'default',
  cancelled: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  paid: 'مدفوع بالكامل',
  partial: 'مدفوع جزئياً',
  pending: 'غير مدفوع',
  cancelled: 'ملغي',
};

const METHOD_LABELS: Record<string, string> = {
  cash: '💵 نقداً',
  card: '💳 كارت',
  insurance: '🏥 تأمين',
  other: 'أخرى',
};

const PaymentPlanModal: React.FC<PaymentPlanModalProps> = ({
  visible,
  onCancel,
  billing: initialBilling,
  onUpdated,
}) => {
  const [billing, setBilling] = useState<Billing>(initialBilling);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const doctorId = localStorage.getItem('doctorId');

  const transactions: PaymentTransaction[] = billing.paymentTransactions || [];
  const amountPaid = billing.amountPaid || 0;
  const remaining = Math.max(0, billing.totalAmount - amountPaid);
  const progressPct = billing.totalAmount > 0 ? Math.min(100, (amountPaid / billing.totalAmount) * 100) : 0;

  // Generates a UUIDv4 for idempotency. Modern browsers expose crypto.randomUUID();
  // we fall back to a timestamp+random combo for legacy contexts.
  const makeOpId = (): string => {
    const c: any = (typeof crypto !== 'undefined') ? crypto : null;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const handleAddPayment = async () => {
    if (!doctorId) { message.error('Doctor ID not found'); return; }
    try {
      const values = await form.validateFields();
      if (values.amount > remaining) {
        message.error(`المبلغ المدخل (${values.amount} ج) أكبر من الرصيد المتبقي (${remaining} ج)`);
        return;
      }
      setSubmitting(true);
      const res = await axios.post(
        `${API.BASE_URL}${API.ENDPOINTS.BILLING_PAYMENT(doctorId, billing.billing_id)}`,
        {
          op_id: makeOpId(),
          amount: values.amount,
          paymentMethod: values.paymentMethod,
          notes: values.notes || '',
          actor: { role: 'doctor', name: values.receivedBy || '' },
        }
      );
      if (res.data.success) {
        // New endpoint returns { billing, event, idempotent }; legacy returned the bill directly.
        const updated: Billing = res.data.data?.billing || res.data.data;
        setBilling(updated);
        onUpdated?.(updated);
        form.resetFields();
        message.success('تم تسجيل الدفعة بنجاح');
        try { window.dispatchEvent(new CustomEvent('revenueChanged')); } catch (_) {}
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'فشل تسجيل الدفعة';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteTransaction = async (transactionId: string) => {
    if (!doctorId) return;
    setDeletingId(transactionId);
    try {
      const res = await axios.delete(
        `${API.BASE_URL}${API.ENDPOINTS.DELETE_BILLING_PAYMENT(doctorId, billing.billing_id, transactionId)}`
      );
      if (res.data.success) {
        const updated: Billing = res.data.data;
        setBilling(updated);
        onUpdated?.(updated);
        message.success('تم حذف الدفعة');
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'فشل حذف الدفعة');
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 40,
      render: (_: any, __: any, index: number) => <Text type="secondary">{index + 1}</Text>,
    },
    {
      title: 'المبلغ',
      dataIndex: 'amount',
      key: 'amount',
      render: (v: number) => <Text strong style={{ color: '#52c41a' }}>{v.toLocaleString()} ج</Text>,
    },
    {
      title: 'طريقة الدفع',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (v: string) => METHOD_LABELS[v] || v,
    },
    {
      title: 'التاريخ',
      dataIndex: 'paidAt',
      key: 'paidAt',
      render: (v: string) =>
        v ? new Date(v).toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' }) : '—',
    },
    {
      title: 'استلم بواسطة',
      dataIndex: 'receivedBy',
      key: 'receivedBy',
      render: (v: string) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'ملاحظات',
      dataIndex: 'notes',
      key: 'notes',
      render: (v: string) => v || <Text type="secondary">—</Text>,
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_: any, record: PaymentTransaction) =>
        billing.paymentStatus !== 'paid' || transactions.length > 1 ? (
          <Popconfirm
            title="حذف هذه الدفعة؟"
            onConfirm={() => handleDeleteTransaction(record.transaction_id)}
            okText="حذف"
            cancelText="إلغاء"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="حذف الدفعة">
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={deletingId === record.transaction_id}
              />
            </Tooltip>
          </Popconfirm>
        ) : null,
    },
  ];

  const statusTag = (
    <Tag color={STATUS_COLORS[billing.paymentStatus] || 'default'} style={{ fontSize: 13, padding: '2px 10px' }}>
      {billing.paymentStatus === 'paid' && <CheckCircleOutlined style={{ marginLeft: 4 }} />}
      {billing.paymentStatus === 'partial' && <ClockCircleOutlined style={{ marginLeft: 4 }} />}
      {billing.paymentStatus === 'pending' && <ExclamationCircleOutlined style={{ marginLeft: 4 }} />}
      {STATUS_LABELS[billing.paymentStatus] || billing.paymentStatus}
    </Tag>
  );

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined style={{ color: '#1890ff' }} />
          <span>سجل المدفوعات — {billing.patient_name}</span>
          {statusTag}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={780}
      footer={[
        <Button key="close" onClick={onCancel}>إغلاق</Button>,
      ]}
    >
      {/* Financial Summary */}
      <Card size="small" style={{ marginBottom: 16, background: '#fafafa', borderRadius: 8 }}>
        <Row gutter={[16, 8]}>
          <Col span={8}>
            <Text type="secondary">إجمالي الفاتورة</Text>
            <div>
              <Text strong style={{ fontSize: 18 }}>{billing.totalAmount.toLocaleString()} ج</Text>
            </div>
          </Col>
          <Col span={8}>
            <Text type="secondary">المدفوع</Text>
            <div>
              <Text strong style={{ fontSize: 18, color: '#52c41a' }}>{amountPaid.toLocaleString()} ج</Text>
            </div>
          </Col>
          <Col span={8}>
            <Text type="secondary">المتبقي</Text>
            <div>
              <Text strong style={{ fontSize: 18, color: remaining > 0 ? '#ff4d4f' : '#52c41a' }}>
                {remaining.toLocaleString()} ج
              </Text>
            </div>
          </Col>
        </Row>
        <div style={{ marginTop: 12 }}>
          <Progress
            percent={Math.round(progressPct)}
            strokeColor={progressPct >= 100 ? '#52c41a' : '#1890ff'}
            format={(pct) => `${pct}%`}
          />
        </div>
      </Card>

      {/* Services breakdown */}
      {billing.services && billing.services.length > 0 && (
        <Alert
          type="info"
          style={{ marginBottom: 16 }}
          message={
            <span>
              الخدمات:{' '}
              {billing.services.map((s, i) => (
                <span key={s.service_id}>
                  {s.service_name} × {s.quantity} ({s.subtotal.toLocaleString()} ج)
                  {i < billing.services.length - 1 ? ' · ' : ''}
                </span>
              ))}
            </span>
          }
        />
      )}

      {/* Payment history table */}
      <Title level={5} style={{ marginBottom: 8 }}>سجل الدفعات</Title>
      {transactions.length === 0 ? (
        <Alert
          type="warning"
          message="لم يتم تسجيل أي دفعات حتى الآن"
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Table
          dataSource={transactions}
          columns={columns}
          rowKey="transaction_id"
          pagination={false}
          size="small"
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Add payment form */}
      {billing.paymentStatus !== 'paid' && billing.paymentStatus !== 'cancelled' && (
        <>
          <Divider>تسجيل دفعة جديدة</Divider>
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="amount"
                  label="المبلغ (ج)"
                  rules={[
                    { required: true, message: 'أدخل المبلغ' },
                    { type: 'number', min: 1, message: 'يجب أن يكون المبلغ أكبر من 0' },
                  ]}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    min={1}
                    max={remaining}
                    placeholder={`بحد أقصى ${remaining.toLocaleString()} ج`}
                    formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="paymentMethod" label="طريقة الدفع" initialValue="cash">
                  <Select>
                    <Option value="cash">💵 نقداً</Option>
                    <Option value="card">💳 كارت</Option>
                    <Option value="insurance">🏥 تأمين</Option>
                    <Option value="other">أخرى</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="receivedBy" label="استلم بواسطة">
                  <Input placeholder="اسم الموظف" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16} align="bottom">
              <Col flex={1}>
                <Form.Item name="notes" label="ملاحظات" style={{ marginBottom: 0 }}>
                  <Input placeholder="ملاحظات اختيارية..." />
                </Form.Item>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  loading={submitting}
                  onClick={handleAddPayment}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a', marginBottom: 0 }}
                >
                  تسجيل الدفعة
                </Button>
              </Col>
            </Row>
          </Form>
        </>
      )}

      {billing.paymentStatus === 'paid' && (
        <Alert
          type="success"
          icon={<CheckCircleOutlined />}
          message="تم سداد الفاتورة بالكامل"
          showIcon
          style={{ marginTop: 8 }}
        />
      )}
    </Modal>
  );
};

export default PaymentPlanModal;
